import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import cn from 'classnames';
import localization from '../../shared/strings';
import { pixelsConfig } from '../DownloadDialog/config';
import {
  clampResizeDimensions,
  heightFromWidth,
  widthFromHeight,
} from '../../helpers/assets/computeLockedResizeDimensions';

function clampDimensionDraft(raw, maxAllowed) {
  if (raw === '') return raw;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return raw;
  if (n > maxAllowed) return String(maxAllowed);
  return raw;
}

export default function AssetsCopyResizePanel({
  width,
  height,
  sourceNaturalWidth,
  sourceNaturalHeight,
  onDimensionsChange,
  disabled,
}) {
  const L = localization.COPY_ASSETS_DIALOG;
  const [wDraft, setWDraft] = useState('');
  const [hDraft, setHDraft] = useState('');

  const pixelWidthInputMax = (
    typeof sourceNaturalWidth === 'number'
      ? Math.min(pixelsConfig.max, sourceNaturalWidth)
      : pixelsConfig.max
  );
  const pixelHeightInputMax = (
    typeof sourceNaturalHeight === 'number'
      ? Math.min(pixelsConfig.max, sourceNaturalHeight)
      : pixelsConfig.max
  );

  useEffect(() => {
    setWDraft(width != null ? String(width) : '');
    setHDraft(height != null ? String(height) : '');
  }, [width, height]);

  const commitPair = useCallback((nextW, nextH, lastEdited) => {
    const clamped = clampResizeDimensions(
      nextW,
      nextH,
      sourceNaturalWidth,
      sourceNaturalHeight,
    );
    if (!clamped) return;
    onDimensionsChange({
      width: clamped.width,
      height: clamped.height,
      lastEdited,
    });
  }, [onDimensionsChange, sourceNaturalHeight, sourceNaturalWidth]);

  const commitFromDrafts = useCallback((lastEdited) => {
    let w = parseInt(wDraft, 10);
    let h = parseInt(hDraft, 10);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
      setWDraft(width != null ? String(width) : '');
      setHDraft(height != null ? String(height) : '');
      return;
    }
    if (lastEdited === 'width' && sourceNaturalWidth && sourceNaturalHeight) {
      h = heightFromWidth(w, sourceNaturalWidth, sourceNaturalHeight);
    } else if (lastEdited === 'height' && sourceNaturalWidth && sourceNaturalHeight) {
      w = widthFromHeight(h, sourceNaturalWidth, sourceNaturalHeight);
    }
    commitPair(w, h, lastEdited);
  }, [
    commitPair,
    hDraft,
    height,
    sourceNaturalHeight,
    sourceNaturalWidth,
    wDraft,
    width,
  ]);

  const handleWDraftChange = useCallback((e) => {
    const raw = clampDimensionDraft(e.target.value, pixelWidthInputMax);
    setWDraft(raw);
    if (raw === '' || !sourceNaturalWidth || !sourceNaturalHeight) return;
    const w = parseInt(raw, 10);
    if (!Number.isFinite(w) || w < 1) return;
    const nextH = heightFromWidth(w, sourceNaturalWidth, sourceNaturalHeight);
    if (nextH != null) setHDraft(String(nextH));
  }, [pixelWidthInputMax, sourceNaturalHeight, sourceNaturalWidth]);

  const handleHDraftChange = useCallback((e) => {
    const raw = clampDimensionDraft(e.target.value, pixelHeightInputMax);
    setHDraft(raw);
    if (raw === '' || !sourceNaturalWidth || !sourceNaturalHeight) return;
    const h = parseInt(raw, 10);
    if (!Number.isFinite(h) || h < 1) return;
    const nextW = widthFromHeight(h, sourceNaturalWidth, sourceNaturalHeight);
    if (nextW != null) setWDraft(String(nextW));
  }, [pixelHeightInputMax, sourceNaturalHeight, sourceNaturalWidth]);

  const handleKeyDown = useCallback((e, lastEdited) => {
    e.stopPropagation();
    const isHeight = lastEdited === 'height';
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const draft = isHeight ? hDraft : wDraft;
      const parsed = parseInt(draft, 10);
      const base = Number.isFinite(parsed) ? parsed : pixelsConfig.min;
      const delta = e.key === 'ArrowUp' ? 1 : -1;
      const max = isHeight ? pixelHeightInputMax : pixelWidthInputMax;
      const nextPrimary = Math.min(max, Math.max(pixelsConfig.min, base + delta));
      if (isHeight) {
        setHDraft(String(nextPrimary));
        if (sourceNaturalWidth && sourceNaturalHeight) {
          const nextW = widthFromHeight(nextPrimary, sourceNaturalWidth, sourceNaturalHeight);
          if (nextW != null) setWDraft(String(nextW));
        }
      } else {
        setWDraft(String(nextPrimary));
        if (sourceNaturalWidth && sourceNaturalHeight) {
          const nextH = heightFromWidth(nextPrimary, sourceNaturalWidth, sourceNaturalHeight);
          if (nextH != null) setHDraft(String(nextH));
        }
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      commitFromDrafts(lastEdited);
      e.target.blur();
    }
  }, [
    commitFromDrafts,
    hDraft,
    pixelHeightInputMax,
    pixelWidthInputMax,
    sourceNaturalHeight,
    sourceNaturalWidth,
    wDraft,
  ]);

  return (
    <div
      className={cn('assetsCopyDialog__resizePanel', disabled && 'assetsCopyDialog__resizePanel--disabled')}
    >
      <div className="assetsCopyDialog__cropDimsRow assetsCopyDialog__resizeDimsRow">
        <TextField
          className="assetsCopyDialog__cropDimField"
          label={L.resizeWidthLabel}
          type="number"
          value={wDraft}
          onChange={handleWDraftChange}
          onBlur={() => commitFromDrafts('width')}
          onKeyDown={(e) => handleKeyDown(e, 'width')}
          disabled={disabled}
          min={pixelsConfig.min}
          max={pixelWidthInputMax}
        />
        <TextField
          className="assetsCopyDialog__cropDimField"
          label={L.resizeHeightLabel}
          type="number"
          value={hDraft}
          onChange={handleHDraftChange}
          onBlur={() => commitFromDrafts('height')}
          onKeyDown={(e) => handleKeyDown(e, 'height')}
          disabled={disabled}
          min={pixelsConfig.min}
          max={pixelHeightInputMax}
        />
      </div>
    </div>
  );
}

AssetsCopyResizePanel.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sourceNaturalWidth: PropTypes.number,
  sourceNaturalHeight: PropTypes.number,
  onDimensionsChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

AssetsCopyResizePanel.defaultProps = {
  width: undefined,
  height: undefined,
  sourceNaturalWidth: undefined,
  sourceNaturalHeight: undefined,
  disabled: false,
};
